import { graphql, type ResultOf, type VariablesOf } from '@newfront-insurance/graphql-frontend';

import { SKILL_GROUP_PEOPLE_FIELDS } from './skill-group-people';

/** Skill bundles are private files, so they need the authenticated contents URL; `generateFileDownloadUrl` only serves public files. */
export const GENERATE_SKILL_BUNDLE_DOWNLOAD_URL = graphql(`
  mutation GenerateSkillBundleDownloadUrl($input: GenerateFileContentsUrlInput!) {
    generateFileContentsUrl(input: $input) {
      downloadUrl
    }
  }
`);

const CUSTOM_SKILL_MANAGEMENT_FIELDS = graphql(`
  fragment CustomSkillManagementFields on CustomSkill @_unmask {
    uuid
    source
    command
    enabled
    sourceFileUuid
    frontmatter {
      name
      description
      summary
      overview
      category
    }
    ownerUserUuid
    owner {
      email
    }
    createdAt
    updatedAt
  }
`);

const CUSTOM_SKILL_OWNER_FIELDS = graphql(`
  fragment CustomSkillOwnerFields on CustomSkill @_unmask {
    owner {
      uuid
      firstName
      lastName
    }
  }
`);

const SKILL_GROUP_FIELDS = graphql(
  `
    fragment SkillGroupManagementFields on SkillGroup @_unmask {
      uuid
      name
      description
      parentGroupUuid
      isOrganization
      createdAt
      updatedAt
      admins
      managers
      members
      inheritedRoles
      skillUuids
      builtInSkillCommandHandles
      skills {
        ...CustomSkillManagementFields
      }
    }
  `,
  [CUSTOM_SKILL_MANAGEMENT_FIELDS],
);

const SKILL_GROUP_SUMMARY_FIELDS = graphql(`
  fragment SkillGroupSummaryManagementFields on SkillGroupSummary @_unmask {
    uuid
    name
    description
    parentGroupUuid
    isOrganization
    createdAt
    updatedAt
  }
`);

export const SEARCH_CUSTOM_SKILLS_FOR_MANAGEMENT = graphql(
  `
    query SearchCustomSkillsForManagement($query: String!, $limit: Int) {
      searchCustomSkillsForManagement(query: $query, limit: $limit) {
        ...CustomSkillManagementFields
      }
    }
  `,
  [CUSTOM_SKILL_MANAGEMENT_FIELDS],
);

export const GET_CUSTOM_SKILL_ACCESS_FOR_MANAGEMENT = graphql(
  `
    query GetCustomSkillAccessForManagement($skillUuid: String!, $shareFilters: CustomSkillShareFiltersInput!) {
      assignments: getCustomSkillGroupAssignments(skillUuid: $skillUuid) {
        skill {
          ...CustomSkillManagementFields
          ...CustomSkillOwnerFields
        }
        groups {
          ...SkillGroupSummaryManagementFields
        }
      }
      shares: getCustomSkillShares(filters: $shareFilters) {
        customSkillUuid
        userUuid
        permission
        createdAt
        updatedAt
      }
    }
  `,
  [CUSTOM_SKILL_MANAGEMENT_FIELDS, CUSTOM_SKILL_OWNER_FIELDS, SKILL_GROUP_SUMMARY_FIELDS],
);

export const GET_SKILL_GROUP = graphql(
  `
    query GetSkillGroup($groupId: String!) {
      getSkillGroup(groupId: $groupId) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export type SkillGroupSummaryRecord = ResultOf<typeof SKILL_GROUP_SUMMARY_FIELDS>;

// Pages use first/after because AWS WAF flags a GraphQL variable named limit as SQL injection.
export const SEARCH_SKILL_GROUPS_FOR_MANAGEMENT = graphql(
  `
    query SearchSkillGroupsForManagement(
      $query: String
      $rootOnly: Boolean
      $parentGroupUuid: String
      $first: Int
      $after: String
    ) {
      searchSkillGroupsForManagement(
        query: $query
        rootOnly: $rootOnly
        parentGroupUuid: $parentGroupUuid
        first: $first
        after: $after
      ) {
        __typename
        groups {
          __typename
          ...SkillGroupSummaryManagementFields
        }
        nextCursor
      }
    }
  `,
  [SKILL_GROUP_SUMMARY_FIELDS],
);

export type SkillGroupSummaryPage = ResultOf<
  typeof SEARCH_SKILL_GROUPS_FOR_MANAGEMENT
>['searchSkillGroupsForManagement'];
export type SearchSkillGroupsForManagementVariables = VariablesOf<typeof SEARCH_SKILL_GROUPS_FOR_MANAGEMENT>;

export const GET_SKILL_GROUP_CHILDREN_FOR_MANAGEMENT = graphql(`
  query GetSkillGroupChildrenForManagement($parentGroupUuid: String!, $limit: Int) {
    getSkillAdministrationOverview(parentGroupUuid: $parentGroupUuid, limit: $limit) {
      groups {
        uuid
        name
        description
      }
    }
  }
`);

export const GET_SKILL_GROUP_PARENT_FOR_MANAGEMENT = graphql(`
  query GetSkillGroupParentForManagement($groupId: String!) {
    getSkillGroup(groupId: $groupId) {
      uuid
      name
      description
    }
  }
`);

export const SEARCH_EMPLOYEES_FOR_SKILL_ACCESS = graphql(`
  query SearchEmployeesForSkillAccess($query: String!, $limit: Int) {
    employees: searchEmployees(query: $query, limit: $limit) {
      userUuid
      email
      firstName
      lastName
      fullName
      imageUrl
      title
    }
  }
`);

const USER_VISIBLE_SKILL_FIELDS = graphql(`
  fragment UserVisibleSkillFields on Skill @_unmask {
    id
    source
    sourceDetails {
      type
      ownerEmail
    }
    command
    name
    description
    summary
    overview
    category
    enabled
    exampleUsage
    instructionPlaceholder
    businessUnit
    roles
    audience
    accountRequired
  }
`);

export const GET_SKILLS_FOR_USER_ACCESS = graphql(
  `
    query GetSkillsForUserAccess($userUuid: String!) {
      skills: getSkillsForUser(userUuid: $userUuid) {
        ...UserVisibleSkillFields
      }
    }
  `,
  [USER_VISIBLE_SKILL_FIELDS],
);

export const GET_EMPLOYEE_SKILL_ACCESS = graphql(
  `
    query GetEmployeeSkillAccess($userUuid: String!, $shareFilters: CustomSkillShareFiltersInput!) {
      skills: getSkillsForUser(userUuid: $userUuid) {
        ...UserVisibleSkillFields
      }
      shares: getCustomSkillShares(filters: $shareFilters) {
        customSkillUuid
        userUuid
        permission
        createdAt
        updatedAt
      }
    }
  `,
  [USER_VISIBLE_SKILL_FIELDS],
);

export const GET_BUILT_IN_SKILL_CATALOG = graphql(
  `
    query GetBuiltInSkillCatalog {
      getBuiltInSkillCatalog {
        ...UserVisibleSkillFields
      }
    }
  `,
  [USER_VISIBLE_SKILL_FIELDS],
);

export const PROVISION_SKILL_GROUP = graphql(
  `
    mutation ProvisionSkillGroup($name: String!, $description: String) {
      provisionSkillGroup(name: $name, description: $description) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

/** Creates a team in an organization. Its creator manages it until they hand it to someone else. */
export const CREATE_SKILL_TEAM = graphql(
  `
    mutation CreateSkillTeam(
      $name: String!
      $description: String
      $parentGroupUuid: String!
      $memberUserUuids: [String!]!
    ) {
      createSkillGroup(
        name: $name
        description: $description
        parentGroupUuid: $parentGroupUuid
        memberUserUuids: $memberUserUuids
      ) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export const UPDATE_SKILL_GROUP = graphql(
  `
    mutation UpdateSkillGroup($groupId: String!, $name: String!, $description: String) {
      updateSkillGroup(groupId: $groupId, name: $name, description: $description) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export const DELETE_SKILL_GROUP = graphql(`
  mutation DeleteSkillGroup($groupId: String!) {
    deleteSkillGroup(groupId: $groupId)
  }
`);

export const SHARE_CUSTOM_SKILL_WITH_GROUP = graphql(
  `
    mutation ShareCustomSkillWithGroup($skillUuid: String!, $groupId: String!) {
      shareCustomSkillWithGroup(skillUuid: $skillUuid, groupId: $groupId) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export const UNSHARE_CUSTOM_SKILL_FROM_GROUP = graphql(
  `
    mutation UnshareCustomSkillFromGroup($skillUuid: String!, $groupId: String!) {
      unshareCustomSkillFromGroup(skillUuid: $skillUuid, groupId: $groupId) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export const SHARE_BUILT_IN_SKILL_WITH_GROUP = graphql(
  `
    mutation ShareBuiltInSkillWithGroup($commandHandle: String!, $groupId: String!) {
      shareBuiltInSkillWithGroup(commandHandle: $commandHandle, groupId: $groupId) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export const UNSHARE_BUILT_IN_SKILL_FROM_GROUP = graphql(
  `
    mutation UnshareBuiltInSkillFromGroup($commandHandle: String!, $groupId: String!) {
      unshareBuiltInSkillFromGroup(commandHandle: $commandHandle, groupId: $groupId) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export const ADD_USER_TO_SKILL_GROUP = graphql(
  `
    mutation AddUserToSkillGroup($groupId: String!, $userUuid: String!, $relation: SkillGroupUserRelation!) {
      addUserToSkillGroup(groupId: $groupId, userUuid: $userUuid, relation: $relation) {
        ...SkillGroupManagementFields
        ...SkillGroupPeopleFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS, SKILL_GROUP_PEOPLE_FIELDS],
);

export const REMOVE_USER_FROM_SKILL_GROUP = graphql(
  `
    mutation RemoveUserFromSkillGroup($groupId: String!, $userUuid: String!, $relation: SkillGroupUserRelation!) {
      removeUserFromSkillGroup(groupId: $groupId, userUuid: $userUuid, relation: $relation) {
        ...SkillGroupManagementFields
        ...SkillGroupPeopleFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS, SKILL_GROUP_PEOPLE_FIELDS],
);

export const ADD_ROLE_TO_SKILL_GROUP = graphql(
  `
    mutation AddRoleToSkillGroup($groupId: String!, $businessRole: String!) {
      addRoleToSkillGroup(groupId: $groupId, businessRole: $businessRole) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export const REMOVE_ROLE_FROM_SKILL_GROUP = graphql(
  `
    mutation RemoveRoleFromSkillGroup($groupId: String!, $businessRole: String!) {
      removeRoleFromSkillGroup(groupId: $groupId, businessRole: $businessRole) {
        ...SkillGroupManagementFields
      }
    }
  `,
  [SKILL_GROUP_FIELDS],
);

export type CustomSkillRecord = ResultOf<
  typeof SEARCH_CUSTOM_SKILLS_FOR_MANAGEMENT
>['searchCustomSkillsForManagement'][number];
export type CustomSkillGroupAssignments = ResultOf<typeof GET_CUSTOM_SKILL_ACCESS_FOR_MANAGEMENT>['assignments'];
export type SkillGroupRecord = ResultOf<typeof GET_SKILL_GROUP>['getSkillGroup'];
export type SkillGroupUserRelation = VariablesOf<typeof ADD_USER_TO_SKILL_GROUP>['relation'];
export type SkillAccessEmployee = ResultOf<typeof SEARCH_EMPLOYEES_FOR_SKILL_ACCESS>['employees'][number];
export type UserVisibleSkill = ResultOf<typeof GET_EMPLOYEE_SKILL_ACCESS>['skills'][number];
export type CustomSkillShareRecord = ResultOf<typeof GET_EMPLOYEE_SKILL_ACCESS>['shares'][number];
